// Copyright 2025, University of Maryland, All Rights Reserved

import React from 'react'
import { cn } from '@/components/ui'
import { ClauseTextBox } from './ClauseTextBox'
import type { ChangeType } from '@/applications/contract-review/types'
import { getChangeLabel, getChangeDisplayText } from '@/applications/contract-review/utils/finding-display'

export interface TextComparisonBoxProps {
  originalText?: string
  changeType?: ChangeType | null
  changeText?: string | null
  targetPhrase?: string | null
  originalLabel?: string
  showCopy?: boolean
  layout?: 'vertical' | 'horizontal'
  className?: string
}

export const TextComparisonBox: React.FC<TextComparisonBoxProps> = ({
  originalText,
  changeType,
  changeText,
  targetPhrase,
  originalLabel = 'Current Text',
  showCopy = true,
  layout = 'vertical',
  className,
}) => {
  const hasChange = changeType && (changeText || targetPhrase)
  if (!originalText && !hasChange) return null

  const isHorizontal = layout === 'horizontal'

  return (
    <div
      className={cn(
        isHorizontal ? 'grid grid-cols-1 md:grid-cols-2 gap-5' : 'space-y-4',
        className
      )}
    >
      {originalText && (
        <ClauseTextBox
          text={originalText}
          label={originalLabel}
          variant="current"
          showCopy={showCopy}
        />
      )}
      {hasChange && (
        <ClauseTextBox
          text={getChangeDisplayText(changeType, changeText ?? null, targetPhrase ?? null)}
          label={getChangeLabel(changeType)}
          variant="recommended"
          showCopy={showCopy}
        />
      )}
    </div>
  )
}
